import axios from 'axios';
import { DebugConfig } from '../config/DebugConfig';

// API Response interface to match apisauce structure
export interface ApiResponse<T = any> {
  ok: boolean;
  data: T;
  status?: number;
  problem?: string;
}

// our "constructor"
const create = (baseURL = "/cdclocalapi") => {
  // ------
  // STEP 1
  // ------
  //
  // Create and configure an axios-based api object.
  //
  const api = axios.create({
    baseURL,
    headers: {
      "Cache-Control": "no-cache",
    },
    timeout: 10000,
  });

  // Set up authentication token from localStorage if available
  const token = localStorage.getItem('accessToken');
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('API: Token loaded from localStorage:', token.substring(0, 20) + '...');
  } else {
    console.log('API: No token found in localStorage');
  }

  // Add request interceptor to ensure token is always included
  api.interceptors.request.use(
    (config) => {
      const currentToken = localStorage.getItem('accessToken');
      if (currentToken && config.headers) {
        config.headers.Authorization = `Bearer ${currentToken}`;
        console.log('API Request Interceptor: Token added to request:', {
          url: config.url,
          method: config.method,
          token: currentToken.substring(0, 20) + '...'
        });
      } else {
        console.log('API Request Interceptor: No token available for request:', {
          url: config.url,
          method: config.method,
          hasToken: !!currentToken,
          hasHeaders: !!config.headers
        });
      }
      return config;
    },
    (error) => {
      console.error('API Request Interceptor Error:', error);
      return Promise.reject(error);
    }
  );

  // Helper function to convert axios response to apisauce-like format
  const convertResponse = <T>(response: any): ApiResponse<T> => ({
    ok: response.status >= 200 && response.status < 300,
    data: response.data,
    status: response.status,
  });

  // Helper function to handle errors
  const handleError = (error: any): ApiResponse => {
    // Log authentication errors for debugging
    if (error.response?.status === 401) {
      console.error('Authentication Error (401):', {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        token: localStorage.getItem('accessToken') ? 'Present' : 'Missing',
        responseData: error.response?.data,
        fullError: error
      });
      
      // Clear invalid token
      if (localStorage.getItem('accessToken')) {
        console.warn('Clearing invalid token due to 401 error');
        clearUserToken();
      }

      // Global redirect to login page on authentication failure
      console.warn('Redirecting to login due to 401 error');
      window.location.href = '/tutorial';
      

    } else {
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data,
        message: error.message
      });
    }
    
    return {
      ok: false,
      data: error.response?.data || error.message,
      status: error.response?.status,
      problem: error.code || 'NETWORK_ERROR',
    };
  };

  // ------
  // STEP 2
  // ------
  //
  // Define some functions that call the api.  The goal is to provide
  // a thin wrapper of the api layer providing nicer feeling functions
  // rather than "get", "post" and friends.
  //
  const getRoot = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getRate = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("rate_limit");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getUser = async (username: string): Promise<ApiResponse> => {
    try {
      const response = await api.get("search/users", { params: { q: username } });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const setUserToken = (userToken: string) => {
    // Set the token in the API headers
    api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
    
    // Store the token in localStorage for persistence
    localStorage.setItem('accessToken', userToken);
    
    // Alternative method to match reference project (if needed):
    // api.setHeader("Authorization", "Bearer " + userToken);
  };

  const clearUserToken = () => {
    // Remove the token from API headers
    delete api.defaults.headers.common['Authorization'];
    
    // Remove the token from localStorage
    localStorage.removeItem('accessToken');
    
    console.log('API: Token cleared from headers and localStorage');
  };

  // Debug function to check authentication state
  const debugAuthState = () => {
    const token = localStorage.getItem('accessToken');
    const headers = api.defaults.headers.common;
    
    console.log('API Debug - Authentication State:', {
      hasLocalStorageToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'None',
      hasAuthHeader: !!headers.Authorization,
      authHeaderPreview: headers.Authorization ? headers.Authorization.substring(0, 30) + '...' : 'None',
      allHeaders: headers
    });
    
    return { hasToken: !!token, hasHeader: !!headers.Authorization };
  };

  const register = async (email: string, childName: string, parentName: string, relation: string): Promise<ApiResponse> => {
    try {
      const response = await api.post("/register", {
        email,
        child_name: childName,
        parent_name: parentName,
        relation,
      });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const login = async (email: string, password: string, notificationToken: string, locale: string): Promise<ApiResponse> => {
    try {
      const response = await api.post("/login", {
        email,
        password,
        notification_token: notificationToken,
        locale,
      });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const forgotPassword = async (email: string): Promise<ApiResponse> => {
    try {
      const response = await api.post("/forgot-password", { email });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const logout = async (): Promise<ApiResponse> => {
    try {
      const response = await api.post("/me/logout");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getMe = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/me");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateNotificationToken = async (token: string): Promise<ApiResponse> => {
    try {
      const response = await api.put("/me/notification_token", { token });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getChildren = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/children");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const attemptGoal = async (id: string): Promise<ApiResponse> => {
    try {
      console.log("attemptGoal", id);
      const response = await api.post(`/goals/${id}/attempt`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateNotesOfGoal = async (id: string, notes: string): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/goals/${id}/notes`, { notes });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const uploadImageToGoal = async (id: string, image: string): Promise<ApiResponse> => {
    try {
      const response = await api.post(`/goals/${id}/images`, { base64: [image] });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const deleteImageFromGoal = async (id: string, imageId: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/goals/${id}/images/${imageId}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getCalendarEvents = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/events");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getAnnouncements = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/announcements");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateProfilePic = async (id: string, image: string): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/children/${id}/profile`, { base64: image });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateUserLocale = async (locale: string): Promise<ApiResponse> => {
    try {
      console.log('API: Updating user locale to:', locale);
      console.log('API: Request payload:', { user_locale: locale });
      
      const response = await api.put("/me/locale", { user_locale: locale });
      console.log('API: Locale update response:', response);
      
      return convertResponse(response);
    } catch (error: any) {
      console.error('API: Locale update error:', {
        error,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // If it's a 500 error, try to provide more specific error information
      if (error.response?.status === 500) {
        console.error('API: Server error (500) - this might be a backend issue');
        console.error('API: Error details:', error.response?.data);
      }
      
      return handleError(error);
    }
  };

  const changePassword = async (password: string, old_password: string): Promise<ApiResponse> => {
    try {
      const response = await api.put("/me/password", { password, old_password });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getMyAppointments = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/appointments");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getAppointmentCategories = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/appointments/categories");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getAppointmentTimeslots = async (categoryId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/appointments/timeslots/${categoryId}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const bookAppointmentTimeslot = async (childId: string, categoryId: string, timeslotId: string): Promise<ApiResponse> => {
    try {
      const response = await api.post(`/appointments/timeslots/${timeslotId}/book`, {
        child_id: childId,
        category_id: categoryId,
      });
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const seenAnAnnouncement = async (announcementId: string): Promise<ApiResponse> => {
    try {
      const response = await api.post(`/announcements/${announcementId}/seen`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getAnnouncementIndividual = async (announcementId: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/announcements/${announcementId}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getAppointmentTimeslotsNew = async (children_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/appointments/timeslots/children/${children_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const leave = async (leave: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/leave", leave);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateLeave = async (leave_id: string, leave: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/leave/${leave_id}`, leave);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const uploadFile = async (leave_id: string, children_id: string, files: any): Promise<ApiResponse> => {
    try {
      console.log('API: uploadFile called with:', { leave_id, children_id, files });
      
      // Handle different file input types
      let formData: FormData;
      
      if (files instanceof FormData) {
        // FormData object (from reference project)
        formData = files;
        console.log('API: Using provided FormData');
      } else if (files instanceof File) {
        // Direct file object
        formData = new FormData();
        formData.append('files', files);
      } else if (files.files && files.files[0]) {
        // FileList from input element
        formData = new FormData();
        formData.append('files', files.files[0]);
      } else if (files.file) {
        // Object with file property
        formData = new FormData();
        formData.append('files', files.file);
      } else if (Array.isArray(files)) {
        // Array of files
        formData = new FormData();
        files.forEach((file, index) => {
          formData.append(`files[]`, file);
        });
      } else if (files && typeof files === 'object' && files.entries) {
        // FormData object with files[] format (from reference project)
        formData = files;
        console.log('API: Using FormData with files[] format');
      } else {
        console.error('API: Invalid file format for uploadFile:', files);
        throw new Error('Invalid file format');
      }
      
      console.log('API: uploadFile sending FormData with entries:', Array.from(formData.entries()));
      
      const response = await api.post(`/leave/${leave_id}/children/${children_id}/file`, formData, {
        // Don't set Content-Type for FormData - browser will set it with boundary
        // headers: { 
        //   "Content-Type": "multipart/form-data"
        // }
      });
      
      console.log('API: uploadFile response:', response);
      return convertResponse(response);
    } catch (error) {
      console.error('API: uploadFile error:', error);
      return handleError(error);
    }
  };

  const deleteFile = async (leave_id: string, children_id: string, file_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/leave/${leave_id}/children/${children_id}/file/${file_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getFile = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/leave/${leave_id}/files`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getLeave = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/leave/${leave_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const deleteLeave = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/leave/${leave_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const applyLeave = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/leave/${leave_id}/apply`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getListLeaves = async (children_id: string, page: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`leaves-app/${children_id}?page=${page}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getListtypes = async (children_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`leaves-history-types-app/${children_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getLeavesMakeUpSession = async (children_id: string, page: string): Promise<ApiResponse> => {
    try {
      console.log('getLeavesMakeUpSession: Calling API with:', {
        children_id,
        page,
        url: `/leaves-make-up-session-app/${children_id}?page=${page}`
      });
      const response = await api.get(`/leaves-make-up-session-app/${children_id}?page=${page}`);

      console.log('getLeavesMakeUpSession: API response:', response);
      return convertResponse(response);
    } catch (error: any) {
      console.error('getLeavesMakeUpSession: API error:', {
        children_id,
        page,
        error: error.response?.data || error.message,
        status: error.response?.status,
        url: error.config?.url
      });
      return handleError(error);
    }
  };

  const getLeavesMakeUpSessionTherapist = async (children_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/leaves-list-of-therapist/${children_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getListHistory = async (type_id: string, children_id: string, page: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`leaves-history-app/type/${type_id}/child/${children_id}?page=${page}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getCheckAvailable = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/make-up-session/check-available-timeslot/leave/${leave_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getCheckAvailableTherapist = async (leave_id: string, idStudent: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/make-up-session/check-available-timeslot/therapist-leave/${leave_id}/${idStudent}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getTimeslots = async (leave_id: string, page: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/appointments/timeslots/in-once-appointment/leave/${leave_id}?page=${page}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getTimeslotsTherapist = async (leave_id: string, saleforce_contact_id_of_children: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/appointments/timeslots/in-once-appointment/leave-therapist/${leave_id}/${saleforce_contact_id_of_children}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const cancelLeave = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/leave/${leave_id}/cancel-leave`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getLeavesCancel = async (children_id: string, page: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/leaves-cancel-app/${children_id}?page=${page}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const createMakeUpSession = async (children_id: any): Promise<ApiResponse> => {
    try {
      const response = await api.post("/make-up-session", children_id);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateMake = async (make_id: string, make: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/make-up-session/${make_id}`, make);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getMake = async (make_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/make-up-session/${make_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const applyMake = async (make_id: string, make: any): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/make-up-session/${make_id}/submit`, make);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getTimeslotsThirtyDay = async (leave_id: string, page: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/appointments/timeslots/thirty-day/leave/${leave_id}?page=${page}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getTimeslotsThirtyDayTherapist = async (leave_id: string, saleforce_contact_id_of_children: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/appointments/timeslots/thirty-day/leave-therapist/${leave_id}/${saleforce_contact_id_of_children}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getMakeUpSession = async (children_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/make-up-session/children/${children_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const cancelMake = async (make_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/make-up-session/${make_id}/cancel`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const uploadMake = async (withdrawal_id: string, withdrawal_file: any): Promise<ApiResponse> => {
    try {
      console.log('API: uploadMake called with:', { withdrawal_id, withdrawal_file });
      
      // Validate withdrawal_id
      if (!withdrawal_id || withdrawal_id === 'withdrawal_form') {
        console.error('API: uploadMake called with invalid withdrawal_id:', withdrawal_id);
        throw new Error('Invalid withdrawal ID');
      }
      
      let formData: FormData;
      
      // Handle different input types
      if (withdrawal_file instanceof FormData) {
        // Already FormData - use as is
        formData = withdrawal_file;
        console.log('API: uploadMake received FormData, using as is');
      } else if (withdrawal_file instanceof File) {
        // Direct file object - create FormData
        formData = new FormData();
        formData.append('withdrawal_file', withdrawal_file);
        console.log('API: uploadMake created FormData from File');
      } else if (withdrawal_file.files && withdrawal_file.files[0]) {
        // FileList from input element - create FormData
        formData = new FormData();
        formData.append('withdrawal_file', withdrawal_file.files[0]);
        console.log('API: uploadMake created FormData from FileList');
      } else if (withdrawal_file.file) {
        // Object with file property - create FormData
        formData = new FormData();
        formData.append('withdrawal_file', withdrawal_file.file);
        console.log('API: uploadMake created FormData from file property');
      } else {
        console.error('API: Invalid file format for uploadMake:', withdrawal_file);
        throw new Error('Invalid file format');
      }
      
      console.log('API: uploadMake sending FormData with file:', formData.get('withdrawal_file'));
      console.log('API: uploadMake endpoint:', `/withdrawal/${withdrawal_id}/upload`);
      console.log('API: uploadMake withdrawal_id type:', typeof withdrawal_id);
      console.log('API: uploadMake withdrawal_id value:', withdrawal_id);
      
      const response = await api.post(`/withdrawal/${withdrawal_id}/upload`, formData, {
        // Don't set Content-Type for FormData - browser will set it with boundary
        // headers: { 
        //   "Content-Type": "multipart/form-data"
        // }
      });
      
      console.log('API: uploadMake response:', response);
      return convertResponse(response);
    } catch (error) {
      console.error('API: uploadMake error:', error);
      return handleError(error);
    }
  };

  const deleteMake = async (withdrawal_id: string, file_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/withdrawals/${withdrawal_id}/file/${file_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const createWithdrawals = async (salesforce_contact_id: any): Promise<ApiResponse> => {
    try {
      console.log('API: createWithdrawals called with:', salesforce_contact_id);
      // Handle both object and direct value formats
      const data = typeof salesforce_contact_id === 'object' ? salesforce_contact_id : { salesforce_contact_id };
      console.log('API: createWithdrawals sending data:', data);
      const response = await api.post("/withdrawals", data);
      console.log('API: createWithdrawals response:', response);
      return convertResponse(response);
    } catch (error) {
      console.error('API: createWithdrawals error:', error);
      return handleError(error);
    }
  };

  const getWithdrawal = async (withdrawal_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/withdrawals/${withdrawal_id}/files`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const applyWithdrawal = async (withdrawal_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.put(`/withdrawals/${withdrawal_id}/submit`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getFileDownload = async (): Promise<ApiResponse> => {
    try {
      const response = await api.get("/withdrawals/download");
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const updateUserNotificationSettings = async (notificationType: string, status: boolean): Promise<ApiResponse> => {
    try {
      // Map frontend notification types to server API keys
      let serverKey: string;
      switch (notificationType) {
        case 'goals':
          serverKey = 'goal_notifications';
          break;
        case 'announcements':
          serverKey = 'announcement_notifications';
          break;
        case 'appointments':
          serverKey = 'appointment_notifications';
          break;
        default:
          console.error('Unknown notification type:', notificationType);
          serverKey = 'unknown';
          // throw new Error(`Unknown notification type: ${notificationType}`);
      }

      console.log('API: Updating notification setting:', { serverKey, status });
      
      // server expects type and status
      const response = await api.put("/me/notification_settings", {
        type: serverKey,
        status: status
      });
      
      console.log('API: Notification setting update response:', response.data);
      return convertResponse(response);
    } catch (error) {
      console.error('API: Error updating notification setting:', error);
      return handleError(error);
    }
  };

  const deleteWithdrawal = async (withdrawal_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.delete(`/withdrawals/${withdrawal_id}`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  const getCheckSickNote = async (leave_id: string): Promise<ApiResponse> => {
    try {
      const response = await api.get(`/leave/${leave_id}/has-sick-note`);
      return convertResponse(response);
    } catch (error) {
      return handleError(error);
    }
  };

  // ------
  // STEP 3
  // ------
  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  // methods in step 2.
  //
  return {
    // a list of the API functions from step 2
    getRoot,
    getRate,
    getUser,
    setUserToken,
    clearUserToken,
    debugAuthState, // Added debugAuthState to the return object
    register,
    forgotPassword,
    login,
    logout,
    getMe,
    updateNotificationToken,
    getChildren,
    attemptGoal,
    updateNotesOfGoal,
    uploadImageToGoal,
    deleteImageFromGoal,
    getCalendarEvents,
    getAnnouncements,
    updateProfilePic,
    updateUserLocale,
    changePassword,
    getMyAppointments,
    getAppointmentCategories,
    getAppointmentTimeslots,
    bookAppointmentTimeslot,
    seenAnAnnouncement,
    getAnnouncementIndividual,
    getAppointmentTimeslotsNew,
    leave,
    updateLeave,
    uploadFile,
    deleteFile,
    getFile,
    getLeave,
    applyLeave,
    getListLeaves,
    deleteLeave,
    getListtypes,
    getListHistory,
    getLeavesMakeUpSession,
    getLeavesMakeUpSessionTherapist,
    getCheckAvailable,
    getCheckAvailableTherapist,
    getTimeslots,
    getTimeslotsTherapist,
    getTimeslotsThirtyDay,
    getTimeslotsThirtyDayTherapist,
    cancelLeave,
    getLeavesCancel,
    createMakeUpSession,
    updateMake,
    getMake,
    applyMake,
    getMakeUpSession,
    cancelMake,
    uploadMake,
    deleteMake,
    createWithdrawals,
    getWithdrawal,
    applyWithdrawal,
    getFileDownload,
    updateUserNotificationSettings,
    deleteWithdrawal,
    getCheckSickNote,
  };
};

// let's return back our create method as the default.
export default {
  create,
};
